/**
 * Typed event registry for PostHog analytics.
 *
 * Every PostHog event MUST be defined here with a Zod schema.
 * Untyped / ad-hoc events are FORBIDDEN.
 *
 * Adding a new event:
 *  1. Define the schema in the appropriate category section below
 *  2. Add it to the EventRegistry at the bottom
 *  3. Use track('EVENT_NAME', { ... }) — TypeScript enforces correctness
 *
 * See Part 22 §121 of the Tech Guidelines.
 */

import { z } from 'zod';

/**
 * How someone signs in or up: an email and password, or one of the social
 * providers — `SOCIAL_PROVIDERS` in @tonnie/foundation, which this leaf package
 * cannot import. The portals pass a `SocialProvider` as `method`, so a provider
 * missing here fails their typecheck.
 */
const authMethod = z.enum(['email', 'google', 'apple', 'microsoft', 'linkedin']);

// ═══════════════════════════════════════════════════════════════════════
// AUTH / CONTEXT
// ═══════════════════════════════════════════════════════════════════════

export const AuthEvents = {
  AUTH_SIGNED_IN: z.object({
    method: z.enum(['clerk', 'sso', 'magic_link']),
  }),
  AUTH_SIGNED_OUT: z.object({}),
  CONTEXT_SWITCHED: z.object({
    from_tenant_id: z.string().optional(),
    to_tenant_id: z.string(),
    from_persona: z.string().optional(),
    to_persona: z.string(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// LOGIN
// ═══════════════════════════════════════════════════════════════════════

export const LoginEvents = {
  /** User submitted the login form (email/password, or a social provider) */
  AUTH_LOGIN_SUBMITTED: z.object({
    method: authMethod,
  }),
  /** Login succeeded — session is now active */
  AUTH_LOGIN_SUCCEEDED: z.object({
    method: authMethod,
  }),
  /** Login attempt failed (wrong credentials, Clerk error, etc.) */
  AUTH_LOGIN_FAILED: z.object({
    method: authMethod,
    error_code: z.string().optional(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// SIGNUP
// ═══════════════════════════════════════════════════════════════════════

export const SignupEvents = {
  /** User submitted the signup registration form */
  AUTH_SIGNUP_SUBMITTED: z.object({
    method: authMethod,
  }),
  /** Email verification OTP was sent to the user */
  AUTH_SIGNUP_VERIFICATION_SENT: z.object({}),
  /** User completed email verification successfully */
  AUTH_SIGNUP_VERIFICATION_COMPLETED: z.object({}),
  /** User requested to resend the verification code */
  AUTH_SIGNUP_VERIFICATION_RESENT: z.object({}),
  /** Signup succeeded — session is now active */
  AUTH_SIGNUP_SUCCEEDED: z.object({
    method: authMethod,
  }),
  /** Signup attempt failed */
  AUTH_SIGNUP_FAILED: z.object({
    method: authMethod,
    error_code: z.string().optional(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// ONBOARDING
// ═══════════════════════════════════════════════════════════════════════

/** What a CV / LinkedIn PDF import found — shared by onboarding and profile. */
const importedFields = z.object({
  firstName: z.boolean(),
  lastName: z.boolean(),
  role: z.boolean(),
  experience: z.number(),
  education: z.number(),
  languages: z.number(),
  skills: z.number(),
  certifications: z.number(),
  websiteUrls: z.number(),
});

export const OnboardingEvents = {
  /** User entered the onboarding wizard */
  ONBOARDING_STARTED: z.object({
    mode: z.enum(['light', 'linkedin', 'full']),
  }),
  /** User advanced to a specific wizard step */
  ONBOARDING_STEP_VIEWED: z.object({
    step_id: z.string(),
    step_index: z.number(),
    total_steps: z.number(),
  }),
  /** User completed a wizard step (clicked next) */
  ONBOARDING_STEP_COMPLETED: z.object({
    step_id: z.string(),
    step_index: z.number(),
    total_steps: z.number(),
  }),
  /** User skipped the onboarding to dashboard */
  ONBOARDING_SKIPPED: z.object({
    step_id: z.string(),
    step_index: z.number(),
    total_steps: z.number(),
  }),
  /** User completed the entire onboarding wizard */
  ONBOARDING_COMPLETED: z.object({
    mode: z.enum(['light', 'linkedin', 'full']),
    total_steps: z.number(),
    linkedin_connected: z.boolean(),
  }),
  /** User connected LinkedIn during onboarding */
  ONBOARDING_LINKEDIN_CONNECTED: z.object({}),
  /** User uploaded a LinkedIn PDF for auto-fill */
  ONBOARDING_LINKEDIN_PDF_UPLOADED: z.object({ fieldsImported: importedFields }),
  /** Onboarding completion API call failed */
  ONBOARDING_FAILED: z.object({
    step_id: z.string(),
    error_type: z.string(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// PROFILE (Settings → Profile sections · citizen first run slice F)
// ═══════════════════════════════════════════════════════════════════════

export const ProfileEvents = {
  /**
   * The citizen saved one section of their profile. How complete it is lives
   * on the server (profile strength), not in the event.
   */
  PROFILE_SECTION_SAVED: z.object({
    section: z.enum(['identity', 'mobility', 'skills', 'education', 'experience']),
  }),
  /** The citizen confirmed a passport / ID card into their profile */
  PROFILE_IDENTITY_DOCUMENT_CONFIRMED: z.object({
    documentType: z.enum(['passport', 'id_card']),
    fieldsApplied: z.number().int().nonnegative(),
  }),
  /** The citizen imported a CV / LinkedIn PDF into their profile */
  PROFILE_IMPORT_COMPLETED: z.object({ fieldsImported: importedFields }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// JOURNEY
// ═══════════════════════════════════════════════════════════════════════

export const JourneyEvents = {
  JOURNEY_CREATED: z.object({
    journey_id: z.string(),
    origin_country: z.string(),
    destination_country: z.string(),
    scenario: z.string(),
  }),
  JOURNEY_ACTIVATED: z.object({
    journey_id: z.string(),
    unlock_mode: z.enum(['free', 'paid', 'sponsored']),
  }),
  SUBSCRIPTION_CHECKOUT_STARTED: z.object({
    journey_id: z.string(),
    plan: z.string(),
    price_id: z.string(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// CHECKUP
// ═══════════════════════════════════════════════════════════════════════

export const CheckUpEvents = {
  CHECKUP_STARTED: z.object({
    journey_id: z.string(),
    talent_id: z.string(),
  }),
  CHECKUP_CHAT_COMPLETED: z.object({
    journey_id: z.string(),
    talent_id: z.string(),
    message_count: z.number(),
  }),
  CHECKUP_COMPLETED: z.object({
    journey_id: z.string(),
    talent_id: z.string(),
    iqulture_score: z.number().min(0).max(100),
    badge: z.enum(['NOOB', 'POTENTIAL', 'PRO']),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// TELEPORTAL
// ═══════════════════════════════════════════════════════════════════════

export const TelePortalEvents = {
  TELEPORTAL_VIEWED: z.object({
    journey_id: z.string(),
    track: z.string(),
  }),
  TELEPORTAL_TASK_COMPLETED: z.object({
    task_id: z.string(),
    task_type: z.string(),
    track_type: z.string(),
  }),
  TELEPORTAL_DOCUMENT_UPLOADED: z.object({
    document_type: z.string(),
    file_size_bytes: z.number(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// CARE
// ═══════════════════════════════════════════════════════════════════════

export const CareEvents = {
  CARE_DAILY_CHECKIN_COMPLETED: z.object({
    checkin_type: z.enum(['mood', 'quick']),
    mood_score: z.number().min(1).max(5).optional(),
  }),
  CARE_CHECKPOINT_COMPLETED: z.object({
    checkpoint_type: z.string(),
    pillar: z.string(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// INOUT
// ═══════════════════════════════════════════════════════════════════════

/**
 * InOut — residence absences. Counts and the product's own enums only: never a
 * destination, a date, a regime or a number of days — where a person goes and
 * when is theirs, not the product's analytics (InOut plan §11).
 */
export const InOutEvents = {
  /** The public calculator answered (before sign-up). */
  INOUT_CALCULATOR_EVALUATED: z.object({
    trips: z.number().int().min(0),
    /** "The rest of the time I was in the country" was ticked. */
    rest_in_host: z.boolean(),
  }),
  /** "What if I travel?" on the public calculator (never saved). */
  INOUT_CALCULATOR_SIMULATED: z.object({
    during_renewal_window: z.boolean(),
    outlasts_title: z.boolean(),
  }),
  /** The calculator loaded on a portal — in a partner's frame, on its own page, or in the app's sheet. */
  INOUT_EMBED_LOADED: z.object({
    portal: z.string().regex(/^[A-Z][A-Z0-9_]{1,39}$/),
    host: z.enum(['frame', 'page', 'app']),
  }),
  /** Keeping the answer on a portal handed the calculation to Tonnie (a one-time token). */
  INOUT_CALCULATOR_HANDED_OFF: z.object({
    portal: z.string().regex(/^[A-Z][A-Z0-9_]{1,39}$/),
  }),
  /** A calculation — from the dialog, or handed off from a portal — became the record, or the InOut was there already. */
  INOUT_CALCULATOR_ADOPTED: z.object({
    outcome: z.enum(['recorded', 'already']),
  }),
  /**
   * A new InOut from a calculation — the calculator's dialog, or one handed
   * off from a portal; the one there, if any, went to the history.
   */
  INOUT_RECORD_STARTED_NEW: z.object({
    from: z.enum(['calculator', 'handoff']),
  }),
  /** The principal InOut closed into the history (H2). */
  INOUT_RECORD_CLOSED: z.object({}),
  /** A closed InOut made the principal again — paused (H1). */
  INOUT_RECORD_RESTORED: z.object({}),
  /** A closed InOut deleted for good (H3). */
  INOUT_RECORD_DELETED: z.object({}),
  /** The InOut paused, or resumed. */
  INOUT_PAUSED: z.object({}),
  INOUT_RESUMED: z.object({}),
  /** The title corrected — or a renewed card added in its place. */
  INOUT_TITLE_UPDATED: z.object({ renewed: z.boolean() }),
  /** The In/Out switch: away from today, or back today. */
  /** The switch's departure undone from its confirmation (FR-REC-3). */
  INOUT_SWITCH_UNDONE: z.object({}),
  INOUT_SWITCH_TOGGLED: z.object({
    to: z.enum(['away', 'back']),
  }),
  /** A trip simulated on the record (never saved). */
  INOUT_TRIP_SIMULATED: z.object({
    during_renewal_window: z.boolean(),
    outlasts_title: z.boolean(),
  }),
  INOUT_TRIP_PLANNED: z.object({}),
  /** The law's way out at the moment of need: the person chose the CoPilot or the agency. */
  INOUT_ASK_BEFORE_LEAVING: z.object({
    where: z.enum(['overview', 'simulation', 'limits']),
    to: z.enum(['copilot', 'agency']),
  }),
  /**
   * A Vault file attached to an absence — by the family of what it proves, and
   * whether it was captured for the trip or picked from the Vault (D1).
   */
  INOUT_EVIDENCE_ATTACHED: z.object({
    family: z.string(),
    from: z.enum(['capture', 'vault']),
  }),
  /** A boarding pass read into a trip (6.1) — which kind of trip, never its dates or places. */
  INOUT_BOARDING_PASS_READ: z.object({
    trip: z.enum(['departure', 'return', 'returnOnly', 'elsewhere']),
  }),
  INOUT_CONFIRMATION_ANSWERED: z.object({
    answer: z.enum(['IN_HOST', 'WAS_AWAY']),
  }),
  INOUT_NOTICE_PREFERENCES_SAVED: z.object({
    push: z.boolean(),
    email: z.boolean(),
  }),
  // The agency funnel (Lane 8): opened → time picked → sharing approved →
  // disclaimer accepted → on to payment → back from it. Counts and enums only.
  /** The overview's advisory-firm card seen — in which state it was (owner, 19 Oct: conversion). */
  INOUT_OFFER_VIEWED: z.object({
    tone: z.enum(['calm', 'review', 'urgent', 'case']),
  }),
  /** A product chosen on that card — on to the advisory firm's tab, booking or buying. */
  INOUT_OFFER_CHOSEN: z.object({
    product: z.enum(['consultation', 'requisition']),
    tone: z.enum(['calm', 'review', 'urgent']),
  }),
  /** The agency tab opened — whether the country's agency takes anyone yet. */
  INOUT_AGENCY_VIEWED: z.object({
    available: z.boolean(),
    cases: z.number().int().min(0),
  }),
  INOUT_CONSULTATION_SLOT_CHOSEN: z.object({
    /** Weeks ahead of this one the time is in. */
    week: z.number().int().min(0),
  }),
  INOUT_CONSENT_GRANTED: z.object({
    purpose: z.enum(['CONSULTATION', 'REQUISITION']),
  }),
  INOUT_CONSENT_REVOKED: z.object({
    purpose: z.enum(['CONSULTATION', 'REQUISITION']),
  }),
  INOUT_LIABILITY_ACCEPTED: z.object({
    product: z.enum(['consultation', 'requisition']),
  }),
  /** On to Stripe — or a requisition the case's credits cover, paid on the spot. */
  INOUT_CHECKOUT_STARTED: z.object({
    product: z.enum(['consultation', 'requisition']),
    covered_by_credit: z.boolean(),
  }),
  INOUT_CHECKOUT_RETURNED: z.object({
    outcome: z.enum(['success', 'cancelled']),
  }),
  /** The agency's calendar could not be read (an honest 503). */
  INOUT_CALENDAR_UNAVAILABLE: z.object({}),
  INOUT_CONTACT_REQUESTED: z.object({
    outcome: z.enum(['requested', 'already_requested']),
  }),
  INOUT_CONSULTATION_MOVED: z.object({
    /** Booked again after the agency cancelled, or a paid time not booked. */
    rebook: z.boolean(),
  }),
  INOUT_REFUND_REQUESTED: z.object({
    /** The country's reason code — never free text. */
    reason: z.string(),
  }),
  INOUT_DOSSIER_DOWNLOADED: z.object({}),
  /** The person exported their own record (PDF) — no case, nothing bought (6.2). */
  INOUT_RECORD_EXPORTED: z.object({}),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// QULTUREUP
// ═══════════════════════════════════════════════════════════════════════

export const QultureUpEvents = {
  QULTUREUP_MISSION_STARTED: z.object({
    mission_id: z.string(),
    world_id: z.string(),
  }),
  QULTUREUP_MISSION_COMPLETED: z.object({
    mission_id: z.string(),
    world_id: z.string(),
    score: z.number().optional(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// CITIZEN (MOBILE-SPECIFIC)
// ═══════════════════════════════════════════════════════════════════════

export const CitizenEvents = {
  COPILOT_MESSAGE_SENT: z.object({
    mode: z.enum(['guide', 'brain', 'agent']),
  }),
  COPILOT_MESSAGE_RECEIVED: z.object({
    mode: z.enum(['guide', 'brain', 'agent']),
    response_time_ms: z.number(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// BUSINESS
// ═══════════════════════════════════════════════════════════════════════

export const BusinessEvents = {
  ATS_CONNECTED: z.object({
    provider: z.string(),
  }),
  CANDIDATE_INVITED: z.object({
    talent_id: z.string(),
    program_id: z.string().optional(),
  }),
  DASHBOARD_VIEWED: z.object({
    dashboard_id: z.string(),
  }),
  JOURNEY_SPONSORED: z.object({
    talent_id: z.string(),
    plan: z.string(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// BACKEND OUTCOME EVENTS (SERVER-SIDE ONLY)
// ═══════════════════════════════════════════════════════════════════════

export const BackendOutcomeEvents = {
  SUBSCRIPTION_ACTIVATED: z.object({
    stripe_subscription_id: z.string(),
    plan: z.string(),
  }),
  ATS_SYNC_COMPLETED: z.object({
    provider: z.string(),
    candidates_synced: z.number(),
    duration_ms: z.number(),
  }),
  IQULTURE_SCORE_GENERATED: z.object({
    talent_id: z.string(),
    score: z.number(),
    badge: z.string(),
    calculation_duration_ms: z.number(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// FEEDBACK
// ═══════════════════════════════════════════════════════════════════════

export const FeedbackEvents = {
  NPS_SUBMITTED: z.object({
    score: z.number().min(0).max(10),
    trigger: z.string(),
  }),
  FEEDBACK_FEATURE_SUBMITTED: z.object({
    category: z.string(),
    hero: z.string().optional(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// PAGE VIEW (AUTO-TRACKED)
// ═══════════════════════════════════════════════════════════════════════

export const NavigationEvents = {
  PAGE_VIEWED: z.object({
    page: z.string(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// MASTER REGISTRY
// ═══════════════════════════════════════════════════════════════════════

/**
 * The single source of truth for all trackable events.
 *
 * To add a new event:
 *  1. Define its Zod schema in the appropriate category above
 *  2. Spread the category into this registry
 *  3. The track() function will enforce type safety at compile time
 *     and validate properties at runtime via Zod
 */
// ═══════════════════════════════════════════════════════════════════════
// COHORT FUNNEL (server-side, PERSON-ANONYMOUS)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Cohort-level progression, for the funnel a business operator builds in
 * PostHog. SERVER-SIDE ONLY.
 *
 * ─── Why these carry NO person identifier ─────────────────────────────────
 *
 * The platform has no PostHog erasure path. `CheckUpEvents.CHECKUP_COMPLETED`
 * and `BackendOutcomeEvents.IQULTURE_SCORE_GENERATED` both require a
 * `talent_id`, so emitting them from the server would put a person identifier
 * into a system that account deletion cannot reach — reintroducing exactly the
 * PII-survives-deletion class `purgeCopilotFootprint` was written to fix.
 *
 * The operator's question is "how is this cohort progressing", which is a
 * COUNTING question and needs no person. So these are keyed on the tenant,
 * carry the cohort, and carry nothing that identifies a human. The per-person
 * truth stays in the cohort-snapshot API, which is access-controlled and
 * covered by the deletion cascade.
 *
 * If per-person funnels are ever genuinely needed, the prerequisite is a
 * PostHog erasure call wired into `softCascadeUserFootprint` — not a quiet
 * widening of these payloads.
 */
export const CohortFunnelEvents = {
  /** A company asked someone for an assessment. */
  COHORT_INVITATION_SENT: z.object({
    cohort_id: z.string(),
    program_id: z.string().optional(),
    invitation_kind: z.string(),
  }),
  /** Someone accepted and their journey went live. */
  COHORT_INVITATION_ACCEPTED: z.object({
    cohort_id: z.string(),
    /** Hours from send to accept — the funnel's latency, with no person in it. */
    hours_to_accept: z.number().optional(),
  }),
  COHORT_CHECKUP_STARTED: z.object({
    cohort_id: z.string().optional(),
  }),
  COHORT_CHECKUP_SUBMITTED: z.object({
    cohort_id: z.string().optional(),
  }),
  /** Scored. The score is a cohort-level metric here, not a person's record. */
  COHORT_CHECKUP_SCORED: z.object({
    cohort_id: z.string().optional(),
    score: z.number().min(0).max(100),
    badge: z.string(),
  }),
} as const;

export const EventRegistry = {
  ...AuthEvents,
  ...LoginEvents,
  ...SignupEvents,
  ...OnboardingEvents,
  ...ProfileEvents,
  ...JourneyEvents,
  ...CheckUpEvents,
  ...TelePortalEvents,
  ...CareEvents,
  ...InOutEvents,
  ...QultureUpEvents,
  ...CitizenEvents,
  ...BusinessEvents,
  ...BackendOutcomeEvents,
  ...CohortFunnelEvents,
  ...FeedbackEvents,
  ...NavigationEvents,
} as const;

/** Union type of all valid event names */
export type EventName = keyof typeof EventRegistry;

/** Helper type: infer properties for a specific event */
export type EventProperties<E extends EventName> = z.infer<(typeof EventRegistry)[E]>;
